import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import InputPriceRate from 'components/others/InputPriceRate';
import spinnerTitle from 'components/others/spinnerTitle';
import SvgSave from 'components/svg/SvgSave';
import HistoryPriceTable from './HistoryPriceTable';
import { COMMENT_MAX_LENGTH, type HistoryPriceFormValues, type HistoryPriceRow } from './model';

interface HistoryPriceFormProps {
    values: HistoryPriceFormValues;
    setFieldValue: (field: string, value: unknown) => void;
    handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
    isSubmitting: boolean;
    priceError?: string;
    history: HistoryPriceRow[];
    historyLoading: boolean;
    isUahMode: boolean;
    rate: number;
    actualCost: number;
    autofocus?: boolean;
}

const HistoryPriceForm = ({
    values,
    setFieldValue,
    handleSubmit,
    isSubmitting,
    priceError,
    history,
    historyLoading,
    isUahMode,
    rate,
    actualCost,
    autofocus = true,
}: HistoryPriceFormProps) => {
    const { t } = useTranslation(['Objects', 'common']);
    const refInput = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!autofocus) return;
        setTimeout(() => refInput.current?.focus(), 100);
    }, [autofocus]);

    const applyActualCost = () => {
        const price = Number(actualCost) || 0;
        setFieldValue('objectPrice', price);
        setFieldValue('objectPriceRate', Math.round(price * rate * 100) / 100);
        setTimeout(() => refInput.current?.focus(), 0);
    };

    return (
        <Form className="d-flex flex-column h-100" onSubmit={handleSubmit}>
            <div className="off-create-team-form d-flex flex-column h-100">
                <div className="off-create-team-content px-3">
                    <Form.Group className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <label
                                className="form-label text-muted mb-0"
                                htmlFor="history-price-input"
                            >
                                {t('historyPrice.labelNewPrice')}
                            </label>
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0 align-baseline"
                                onClick={applyActualCost}
                            >
                                {t('historyPrice.actualCost')}
                            </button>
                        </div>
                        <InputPriceRate
                            ref={refInput}
                            value={values.objectPrice || 0}
                            allowNegative={false}
                            onValueChange={(val: { floatValue?: number; value?: string }) => {
                                const price = Number(val?.floatValue ?? val?.value) || 0;
                                setFieldValue('objectPrice', price);
                                setFieldValue(
                                    'objectPriceRate',
                                    Math.round(price * rate * 100) / 100,
                                );
                            }}
                        />
                        {priceError ? (
                            <div className="invalid-feedback d-block">{priceError}</div>
                        ) : null}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <label className="form-label text-muted" htmlFor="history-price-comment">
                            {t('historyPrice.labelComment')}
                        </label>
                        <Form.Control
                            as="textarea"
                            id="history-price-comment"
                            rows={3}
                            value={values.comment}
                            maxLength={COMMENT_MAX_LENGTH}
                            onChange={(e) =>
                                setFieldValue(
                                    'comment',
                                    e.target.value.slice(0, COMMENT_MAX_LENGTH),
                                )
                            }
                        />
                        <div className="form-text text-end">
                            {String(values.comment || '').length} / {COMMENT_MAX_LENGTH}
                        </div>
                    </Form.Group>

                    <div className="mb-2">
                        <div className="text-muted small mb-2">{t('historyPrice.historyTitle')}</div>
                        <HistoryPriceTable
                            rows={history}
                            loading={historyLoading}
                            isUahMode={isUahMode}
                        />
                    </div>
                </div>

                <div className="p-3 offcanvas-footer off-create-team-footer">
                    <div className="d-flex justify-content-end">
                        <Button
                            type="submit"
                            variant="info"
                            disabled={isSubmitting}
                            className="min100px"
                        >
                            {spinnerTitle(
                                <>
                                    <SvgSave className="me-2 mtop-2" />
                                    {t('common:buttons.save')}
                                </>,
                                isSubmitting,
                                t('common:buttons.saving'),
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </Form>
    );
};

export default HistoryPriceForm;
